import { cn } from "@/lib/utils";
import type {
	CycleStageItem,
	CycleStageState,
	CycleStageStatus,
} from "@payrollmart-invoice/shared";
import { ArrowRight, Check, ChevronRight, Clock, Layers, TriangleAlert } from "lucide-react";

const STATE_DOT_COLOR: Record<CycleStageState, string> = {
	complete: "#16a34a",
	active: "#4f46e5",
	waiting: "#9ca3af",
};

const STATE_LABEL: Record<CycleStageState, string> = {
	complete: "Complete",
	active: "Active",
	waiting: "Waiting",
};

interface StageTimelineProps {
	stages: CycleStageStatus[];
}

/**
 * Stage-by-stage timeline for the monthly cycle. Renders a 4-row card,
 * one row per canonical stage (Upload → Extract → Review → Push). Copy and
 * CTA come from the server payload so the UI is always in sync with real
 * counts.
 */
export function StageTimeline({ stages }: StageTimelineProps) {
	return (
		<div className="rounded-lg border border-[#e5e7eb] bg-white" data-testid="stage-timeline">
			<div className="flex items-center justify-between border-b border-[#e5e7eb] px-6 py-4">
				<div className="flex items-center gap-2">
					<Layers className="h-4 w-4 text-[#6b7280]" strokeWidth={1.5} />
					<h2 className="text-sm font-semibold text-[#1e1e20]">Stage-by-stage</h2>
				</div>
				<span className="text-[11px] text-[#9ca3af]">{stages.length} stages</span>
			</div>
			<div className="px-6">
				{stages.map((stage, idx) => (
					<StageRow key={stage.key} stage={stage} isFirst={idx === 0} />
				))}
			</div>
		</div>
	);
}

function StageRow({ stage, isFirst }: { stage: CycleStageStatus; isFirst: boolean }) {
	return (
		<div
			data-testid="stage-row"
			data-stage-key={stage.key}
			className={cn(
				"grid gap-6 py-5 md:grid-cols-[180px_1fr]",
				!isFirst && "border-t border-[#f3f4f6]",
			)}
		>
			<div>
				<div className="mb-1.5 flex items-center gap-2">
					<span
						className="h-2.5 w-2.5 rounded-full"
						style={{ backgroundColor: STATE_DOT_COLOR[stage.state] }}
					/>
					<span className="text-[10px] font-medium uppercase tracking-[0.08em] text-[#9ca3af]">
						{STATE_LABEL[stage.state]}
					</span>
				</div>
				<div className="text-base font-semibold tracking-tight text-[#1e1e20]">{stage.name}</div>
				<div className="mt-1 text-xs tabular-nums text-[#6b7280]">{stage.dateRange}</div>
			</div>
			<div>
				<div className="text-[15px] font-medium text-[#1e1e20]">{stage.primary}</div>
				<div className="mt-1 text-[13px] leading-relaxed text-[#6b7280]">{stage.secondary}</div>
				{stage.items.length > 0 && (
					<div className="mt-3.5 flex flex-col gap-2">
						{stage.items.map((item) => (
							<StageItemRow key={item.label} item={item} />
						))}
					</div>
				)}
				{stage.cta && (
					<a
						href={stage.cta.href}
						className="mt-3.5 inline-flex items-center gap-1.5 rounded-md bg-[#4f46e5] px-3 py-1.5 text-xs font-medium text-white transition-colors hover:bg-[#4338ca]"
					>
						{stage.cta.label}
						<ArrowRight className="h-3 w-3" strokeWidth={2} />
					</a>
				)}
			</div>
		</div>
	);
}

function StageItemRow({ item }: { item: CycleStageItem }) {
	const Icon = item.status === "ok" ? Check : item.status === "warn" ? TriangleAlert : Clock;
	const iconColor =
		item.status === "ok" ? "#16a34a" : item.status === "warn" ? "#d97706" : "#9ca3af";

	const body = (
		<>
			<Icon className="h-3.5 w-3.5 shrink-0" strokeWidth={2} style={{ color: iconColor }} />
			<span className="text-[12.5px] font-medium text-[#1e1e20]">{item.label}</span>
			<span className="ml-auto text-xs text-[#6b7280]">{item.meta}</span>
			{item.href && (
				<ChevronRight className="h-3.5 w-3.5 text-[#9ca3af]" strokeWidth={2} aria-hidden />
			)}
		</>
	);

	if (item.href) {
		return (
			<a
				href={item.href}
				className="flex items-center gap-3 rounded-md border border-[#e5e7eb] bg-[#f8f9fb] px-3 py-2 transition-colors hover:border-[#4f46e5] hover:bg-white focus-visible:border-[#4f46e5] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#4f46e5]/20"
			>
				{body}
			</a>
		);
	}

	return (
		<div className="flex items-center gap-3 rounded-md border border-[#e5e7eb] bg-[#f8f9fb] px-3 py-2">
			{body}
		</div>
	);
}
